import {isNullOrEmpty, UserError} from "@2d-front-monorepo/core";
import {CreditCard, CreditCardProvider} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {useCallback, useEffect, useMemo, useRef} from "react";
import {API_ENDPOINT} from "../api-endpoint";
import {formatRoute} from "../routes";
import getInstallationId from "./getInstallationId";
import {usePermits} from "./usePermits";

type ChannelData = {
	from: string;
	externalId: string;
	error?: string | undefined;
	requestId?: string | undefined;
	traceId?: string | undefined;
}

class CreditCardAssignmentEngine {
	private _broadcastChannel?: BroadcastChannel;
	private readonly _cardProvider: CreditCardProvider;
	private _checkClosed: number;
	private readonly _externalId: string;
	private _openedWindow: Window | null | undefined;
	private readonly _plates: string[];
	private _reject: ((reason?: any) => void) | undefined;
	private _resolve: ((value: (PromiseLike<string> | string)) => void) | undefined;
	private _windowMessageHandler?: (event: MessageEvent) => void;

	constructor(cardProvider: CreditCardProvider, externalId: string, plates?: string[]) {
		this._cardProvider = cardProvider;
		this._externalId = externalId;
		this._plates = plates ?? [];
		this._checkClosed = -1;
	}

	public startRegister(): Promise<string> {
		this._broadcastChannel = this.registerBroadcastChannel();
		this._windowMessageHandler = this.registerWindowMessageListener();
		return new Promise<string>((resolve, reject) => {
			this._resolve = resolve;
			this._reject = reject;
			const plates = this._plates.join(",");
			const installationId = getInstallationId();
			this._openedWindow = window.open(formatRoute(API_ENDPOINT.registerPayment, this._cardProvider, `${location.origin}/payment.html`, this._externalId, plates, installationId), "_blank");
			if (this._openedWindow != null) {
				this._checkClosed = setInterval(() => {
					if (this._openedWindow && this._openedWindow.closed) {
						clearInterval(this._checkClosed);
						this._dispose();
						reject(new UserError(
							{text: PermitTranslations.payments_page__close_window__title},
							{text: PermitTranslations.payments_page__close_window__description},
							[],
							{text: PermitTranslations.payments_page__close_window__footer})
						);
					}
				}, 250);

			} else {
				this._dispose();
				reject(new UserError(
					{text: PermitTranslations.payments_page__popup_error__title},
					{text: PermitTranslations.payments_page__popup_error__description},
					[],
					{text: PermitTranslations.payments_page__popup_error__footer})
				);
			}
		});
	}

	private _dispose(): void {
		this._broadcastChannel?.close();
		clearInterval(this._checkClosed);
		if (this._windowMessageHandler) {
			window.removeEventListener("message", this._windowMessageHandler);
			this._windowMessageHandler = undefined;
		}
	}

	private _handlePayload(data: ChannelData): void {
		// "permits_channel" is shared: another tab's registration must not settle this one.
		if (data.externalId !== this._externalId) {
			return;
		}

		// Acknowledge so payment.html can stop re-broadcasting and close itself; this
		// is the primary popup-close path since browsers often refuse window.close()
		// from the opener after a cross-origin payment-provider round-trip.
		try {
			this._broadcastChannel?.postMessage({ack: data.externalId});
		} catch {
			// channel may already be closed
		}

		const {error, externalId, requestId, traceId} = data;
		clearInterval(this._checkClosed);
		try {
			this._openedWindow?.close();
		} catch { /* secondary best-effort close */
		}
		this._dispose();

		if (isNullOrEmpty(error)) {
			if (this._resolve) {
				this._resolve(externalId);
			}

		} else {
			if (!this._reject) {
				return;
			}

			this._reject(new UserError(
				{text: PermitTranslations.payments_page__authorization_error__title},
				{text: PermitTranslations.payments_page__authorization_error__description},
				[
					{text: error},
					{text: "ꭲꮖꭰ: {value}", args: {"value": traceId}},
					{text: "ꭱꮖꭰ: {value}", args: {"value": requestId}}
				],
				{text: PermitTranslations.payments_page__authorization_error__footer}
			));
		}
	}

	private registerBroadcastChannel(): BroadcastChannel {
		const broadcastChannel = new BroadcastChannel("permits_channel");
		broadcastChannel.onmessage = (event: MessageEvent) => {
			const data = event.data as (ChannelData & { ack?: string });
			// Ignore our own ACK echoes.
			if (data?.ack !== undefined) {
				return;
			}
			this._handlePayload(data);
		}

		return broadcastChannel;
	}

	// Secondary transport: payment.html also calls window.opener.postMessage with the
	// same payload. This survives the rare case where BroadcastChannel drops a message
	// (observed when the popup closes its channel synchronously after postMessage).
	private registerWindowMessageListener(): (event: MessageEvent) => void {
		const handler = (event: MessageEvent) => {
			if (event.origin !== location.origin) return;
			const data = event.data as (ChannelData & { source?: string }) | null;
			if (!data || data.source !== "permits_payment_channel") return;
			this._handlePayload(data);
		};
		window.addEventListener("message", handler);
		return handler;
	}
}

export type CreditCardAssignment = {
	creditCard: CreditCard;
	registerCreditCard: () => Promise<string>;
}

export function useCreditCardAssignment(externalId: string, plates?: string[]): CreditCardAssignment {
	const {holderData} = usePermits();
	//an installation without card providers keeps the store default (providers: []) — mounting
	//the payment UI must not crash; registering will fail server-side with a real error instead
	const defaultCardProvider = holderData.paymentProviders.providers[0]?.id ?? CreditCardProvider.Undefined;
	const creditCard = holderData.paymentProviders.creditCard;

	const engine = useRef<CreditCardAssignmentEngine>(null);

	useEffect(() => {
		engine.current ??= new CreditCardAssignmentEngine(defaultCardProvider, externalId, plates);
	}, [defaultCardProvider, externalId, plates]);

	// registerCreditCard only ever touches the (stable) engine ref, so it is stable too.
	const registerCreditCard = useCallback(function (): Promise<string> {
		return engine.current?.startRegister() ?? Promise.reject("engine_not_assigned");
	}, []);

	// Return a stable object: consumers use this as a useCallback/useMemo dependency, and a
	// fresh literal every render would invalidate their memoized submit/validation callbacks.
	return useMemo(() => ({creditCard, registerCreditCard}), [creditCard, registerCreditCard]);
}

export function useCreditCard(): CreditCard | undefined {
	const {holderData} = usePermits();
	const cc = holderData.paymentProviders.creditCard;
	return cc == null || cc.provider === CreditCardProvider.Undefined ? undefined : cc;
}
